import * as React from 'react';
import propz from 'propz';
import { LIMIT } from 'Src/consts/table';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import {
  getClubStudents,
  getClubStudentsCount,
  getClubStudentsPost,
  getClubStudentsCountPost
} from 'Src/helpers/service/admin/clubStudents';
import { getSelectOptionForAge, getSelectOptionForForms, getSelectOptionForGender } from 'Src/helpers/table/select';
import { getPersonListSortedByName } from 'Src/helpers/table/table';
import { AppUser } from 'Src/views/App/App';
import { getErrMsg } from 'Src/helpers/clubProgramme/clubProgramme';
import { getUserGenderArrayConvertedFromEventGender } from 'Src/helpers/tournament/tournament';
import { INVITE_FILTERS_EMPTY, INVITE_ITEMS_COLUMNS } from '../helpers/clubProgrammeClubsContent';

const BATCH_SIZE = 50;

type Params = {
  user: AppUser;
  programmeId: string;
  ageGroupsNaming: any;
  onProgrammeUpdated: (p: any) => void;
  setSaving: (programmeClubId: string, v: boolean) => void;
  updateProgrammeClub: (user: AppUser, programmeId: string, programmeClubId: string, data: any) => Promise<any>;
};

export const useClubProgrammeManageClubsInvite = ({
  user,
  programmeId,
  ageGroupsNaming,
  onProgrammeUpdated,
  setSaving,
  updateProgrammeClub
}: Params) => {
  const [isInviteListOpen, setIsInviteListOpen] = React.useState(false);
  const [inviteProgrammeClubId, setInviteProgrammeClubId] = React.useState<string>('');
  const [inviteClubId, setInviteClubId] = React.useState<string>('');
  const [inviteInvitesOnly, setInviteInvitesOnly] = React.useState<boolean>(false);
  const [inviteClubName, setInviteClubName] = React.useState<string>('');

  const [inviteInvitedItems, setInviteInvitedItems] = React.useState<any[]>([]);
  const [inviteInvitedSelected, setInviteInvitedSelected] = React.useState<any[]>([]);
  const [inviteEligibleSelected, setInviteEligibleSelected] = React.useState<any[]>([]);
  const [inviteInitialInvitedIds, setInviteInitialInvitedIds] = React.useState<Set<string>>(new Set());

  const [inviteEligible, setInviteEligible] = React.useState<any[]>([]);
  const [inviteEligibleLoading, setInviteEligibleLoading] = React.useState(false);
  const [inviteInvitedLoading, setInviteInvitedLoading] = React.useState(false);
  const [inviteErr, setInviteErr] = React.useState<string>('');

  const [inviteSelectedPermIds, setInviteSelectedPermIds] = React.useState<{ [permId: string]: boolean }>({});
  const [inviteSelectedByPermId, setInviteSelectedByPermId] = React.useState<{
    [permId: string]: { userId: string; permissionId: string };
  }>({});

  const [inviteItemsAddBuffer, setInviteItemsAddBuffer] = React.useState<any[]>([]);
  const [inviteItemsRemoveBuffer, setInviteItemsRemoveBuffer] = React.useState<any[]>([]);

  const [inviteSchoolForms, setInviteSchoolForms] = React.useState<any[]>([]);
  const [invitePage, setInvitePage] = React.useState(1);
  const [inviteTotal, setInviteTotal] = React.useState(0);
  const [inviteShowEligibleFilter, setInviteShowEligibleFilter] = React.useState(false);
  const [inviteFiltersDraft, setInviteFiltersDraft] = React.useState<any>(INVITE_FILTERS_EMPTY);
  const [inviteFiltersApplied, setInviteFiltersApplied] = React.useState<any>(INVITE_FILTERS_EMPTY);
  const [invitePresetFilters, setInvitePresetFilters] = React.useState<Record<string, string[]>>({});
  const [inviteSearch, setInviteSearchState] = React.useState('');
  const [inviteSearchApplied, setInviteSearchApplied] = React.useState('');
  const setInviteSearch = React.useCallback((value: string) => {
    setInviteSearchState(value);
    setInviteSearchApplied(value.trim());
  }, []);

  // ID уже приглашённых — для передачи на сервер через $nin в POST body
  const inviteExcludeIds = React.useMemo(() => {
    return Array.from(inviteInitialInvitedIds);
  }, [inviteInitialInvitedIds]);

  React.useEffect(() => {
    if (!isInviteListOpen) return;

    getAllForms(user)
      .then((res: any) => {
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.forms)
          ? res.forms
          : [];
        setInviteSchoolForms(list);
      })
      .catch(() => setInviteSchoolForms([]));
  }, [isInviteListOpen, user]);

  const inviteItemsFiltersOptions = React.useMemo(() => {
    const agePresetValues = new Set((invitePresetFilters.ages || []).map(String));
    const genderPresetValues = new Set((invitePresetFilters.gender || []).map(String));
    const formsForClubAges =
      agePresetValues.size > 0
        ? (inviteSchoolForms || []).filter(form => agePresetValues.has(String(form?.age)))
        : inviteSchoolForms || [];
    return {
      forms: getSelectOptionForForms(formsForClubAges),
      ages: getSelectOptionForAge(inviteSchoolForms || [], ageGroupsNaming).filter(option =>
        agePresetValues.has(String(option.value))
      ),
      gender: getSelectOptionForGender().filter(option => genderPresetValues.has(String(option.value)))
    } as any;
  }, [inviteSchoolForms, ageGroupsNaming, invitePresetFilters]);

  const inviteAgeLabelToValue = React.useMemo(() => {
    const map: Record<string, string> = {};
    const opts = (inviteItemsFiltersOptions as any)?.ages || [];
    (opts || []).forEach((o: any) => {
      const v = String(o?.value ?? '');
      const l = String(o?.label ?? '');
      if (l && v) map[l] = v;
    });
    return map;
  }, [inviteItemsFiltersOptions]);

  const buildInviteWhere = React.useCallback(
    (filtersApplied: any, searchApplied = '') => {
      const where: any = {};

      // $nin передаётся через POST body на сервер — здесь не нужен

      const fFirst = String(filtersApplied?.firstName || '').trim();
      const fLast = String(filtersApplied?.lastName || '').trim();

      const and: any[] = [];
      if (fFirst) and.push({ firstName: { like: fFirst, options: 'i' } });
      if (fLast) and.push({ lastName: { like: fLast, options: 'i' } });
      if (and.length) propz.set(where, ['$and'], and);

      const search = String(searchApplied || '').trim();
      if (search) {
        propz.set(
          where,
          ['$or'],
          [{ firstName: { like: search, options: 'i' } }, { lastName: { like: search, options: 'i' } }]
        );
      }

      const selectedForms: string[] = Array.isArray(filtersApplied?.forms) ? filtersApplied.forms.map(String) : [];
      const selectedAgesRaw: any[] = Array.isArray(filtersApplied?.ages) ? filtersApplied.ages : [];

      const agesNums: number[] = selectedAgesRaw
        .map(a => {
          const raw = String(a ?? '');
          const n = Number(raw);
          if (!Number.isNaN(n)) return n;
          const mapped = inviteAgeLabelToValue[raw];
          const n2 = Number(mapped);
          return Number.isNaN(n2) ? null : n2;
        })
        .filter((x: any) => x !== null) as number[];

      const formIdsByAge: string[] =
        agesNums.length > 0
          ? (inviteSchoolForms || [])
              .filter((f: any) => agesNums.includes(Number(f?.age)))
              .map((f: any) => String(f?.id || f?._id || ''))
              .filter(Boolean)
          : [];

      let finalFormIds: string[] = [];
      if (selectedForms.length > 0 && formIdsByAge.length > 0) {
        const setA = new Set(formIdsByAge);
        finalFormIds = selectedForms.filter(id => setA.has(id));
      } else if (selectedForms.length > 0) {
        finalFormIds = selectedForms;
      } else if (formIdsByAge.length > 0) {
        finalFormIds = formIdsByAge;
      }

      if (finalFormIds.length > 0) {
        propz.set(where, ['formId', '$in'], finalFormIds);
      } else if (selectedForms.length > 0 || agesNums.length > 0) {
        propz.set(where, ['formId', '$in'], ['__none__']);
      }

      const selectedGenders = Array.isArray(filtersApplied?.gender)
        ? filtersApplied.gender.map(String).filter(Boolean)
        : [];
      if (selectedGenders.length > 0) propz.set(where, ['gender', '$in'], selectedGenders);

      return where;
    },
    [inviteSchoolForms, inviteAgeLabelToValue]
  );

  // строим where с $nin для передачи на сервер
  const buildInviteWhereWithExclude = React.useCallback(
    (filtersApplied: any, searchApplied = '') => {
      const where = buildInviteWhere(filtersApplied, searchApplied);
      if (inviteExcludeIds.length > 0) {
        propz.set(where, ['_id', '$nin'], inviteExcludeIds);
      }
      return where;
    },
    [buildInviteWhere, inviteExcludeIds]
  );

  const loadInvitePage = React.useCallback(
    (page: number, filtersAppliedOverride?: any, searchAppliedOverride?: string) => {
      if (!inviteClubId) return;

      const applied = filtersAppliedOverride ?? inviteFiltersApplied;
      const appliedSearch = searchAppliedOverride ?? inviteSearchApplied;
      const where = buildInviteWhereWithExclude(applied, appliedSearch);
      const safePage = page < 1 ? 1 : page;

      setInviteEligibleLoading(true);
      setInviteErr('');

      getClubStudentsCountPost(user, inviteClubId, where)
        .then((cnt: any) => {
          const total = Number(cnt?.count || 0);
          setInviteTotal(total);

          const maxPages = Math.max(1, Math.ceil(total / LIMIT));
          const finalPage = Math.min(safePage, maxPages);
          const finalSkip = (finalPage - 1) * LIMIT;

          return getClubStudentsPost(user, inviteClubId, { limit: LIMIT, skip: finalSkip, where } as any).then(
            (list: any[]) => {
              setInviteEligible(Array.isArray(list) ? list : []);
              setInvitePage(finalPage);
              setInviteEligibleLoading(false);
            }
          );
        })
        .catch((e: any) => {
          setInviteEligible([]);
          setInviteTotal(0);
          setInviteEligibleLoading(false);
          setInviteErr(getErrMsg(e, 'Failed to load eligible students'));
        });
    },
    [user, inviteClubId, inviteFiltersApplied, inviteSearchApplied, buildInviteWhereWithExclude]
  );

  const loadAllInviteEligible = React.useCallback(
    (filtersAppliedOverride?: any, searchAppliedOverride?: string) => {
      if (!inviteClubId) return Promise.resolve([]);

      const applied = filtersAppliedOverride ?? inviteFiltersApplied;
      const appliedSearch = searchAppliedOverride ?? inviteSearchApplied;
      const where = buildInviteWhereWithExclude(applied, appliedSearch);

      return getClubStudentsPost(user, inviteClubId, { limit: 10000, skip: 0, where } as any).then((list: any[]) =>
        Array.isArray(list) ? list : []
      );
    },
    [user, inviteClubId, inviteFiltersApplied, inviteSearchApplied, buildInviteWhereWithExclude]
  );

  React.useEffect(() => {
    if (!isInviteListOpen) return;
    if (!inviteClubId) return;

    setInviteEligible([]);
    setInviteTotal(0);
    loadInvitePage(1);
  }, [isInviteListOpen, inviteClubId, loadInvitePage]);

  const openInviteList = React.useCallback(
    (row: any) => {
      const pcId = String(row?.programmeClubId || '');
      const clubId = String(row?.clubId || '');
      if (!pcId || !clubId) return;

      setInviteErr('');
      setInviteProgrammeClubId(pcId);
      setInviteClubId(clubId);
      setInviteInvitesOnly(!!row.invitesOnly);
      setInviteClubName(String(row?.name || row?.club?.name || 'Club'));
      const clubGender = propz.get(row, ['club', 'gender'], '');
      const clubGenderValues = getUserGenderArrayConvertedFromEventGender(clubGender) || [];
      const genderPreset = clubGenderValues.length === 1 ? clubGenderValues.map(String) : [];
      const agePreset = (propz.get<any[]>(row, ['club', 'ages'], []) || []).map(String).filter(Boolean);
      const nextPresetFilters = {
        ...(genderPreset.length > 0 ? { gender: genderPreset } : {}),
        ...(agePreset.length > 0 ? { ages: agePreset } : {})
      };
      const initialFilters = { ...INVITE_FILTERS_EMPTY, ...nextPresetFilters };
      setInvitePresetFilters(nextPresetFilters);

      const allow = row?.pc && (row.pc as any).allowList ? (row.pc as any).allowList : [];
      const nextSel: any = {};
      const nextMap: any = {};

      (Array.isArray(allow) ? allow : []).forEach((x: any) => {
        const pid = String(x?.permissionId || '');
        const uid = String(x?.userId || '');
        if (pid) {
          nextSel[pid] = true;
          if (uid) nextMap[pid] = { userId: uid, permissionId: pid };
        }
      });

      setInviteSelectedPermIds(nextSel);
      setInviteSelectedByPermId(nextMap);

      const invitedUserIds = (Array.isArray(allow) ? allow : [])
        .map((x: any) => String(x?.userId || ''))
        .filter(Boolean);

      setInviteInitialInvitedIds(new Set(invitedUserIds));

      if (invitedUserIds.length) {
        setInviteInvitedLoading(true);
        const batches: string[][] = [];
        for (let i = 0; i < invitedUserIds.length; i += BATCH_SIZE) {
          batches.push(invitedUserIds.slice(i, i + BATCH_SIZE));
        }

        Promise.all(
          batches.map(batch =>
            getClubStudentsPost(user, clubId, {
              limit: batch.length,
              skip: 0,
              where: { _id: { $in: batch } }
            } as any)
          )
        )
          .then((results: any[][]) => {
            const users = ([] as any[]).concat(...results).filter(Boolean);
            const byUserIdToPermId: any = {};
            (Array.isArray(allow) ? allow : []).forEach((x: any) => {
              const uid = String(x?.userId || '');
              const pid = String(x?.permissionId || '');
              if (uid && pid) byUserIdToPermId[uid] = pid;
            });

            const invitedItems = users.map((u: any) => ({
              ...u,
              id: String(u?.id || u?._id || ''),
              permissionId: byUserIdToPermId[String(u?.id || u?._id || '')] || String(u?.permissionId || '')
            }));

            setInviteInvitedItems(invitedItems.filter((x: any) => !!x.id && !!x.permissionId));
            setInviteInvitedSelected([]);
            setInviteInvitedLoading(false);
            setInviteEligibleSelected([]);
          })
          .catch(() => {
            setInviteInvitedItems([]);
            setInviteInvitedLoading(false);
          });
      } else {
        setInviteInvitedItems([]);
        setInviteInvitedSelected([]);
        setInviteEligibleSelected([]);
      }

      setInviteFiltersDraft(initialFilters);
      setInviteFiltersApplied(initialFilters);
      setInviteSearch('');
      setInviteSearchApplied('');
      setInvitePage(1);
      setInviteTotal(0);
      setInviteEligible([]);
      setInviteItemsAddBuffer([]);
      setInviteItemsRemoveBuffer([]);
      setIsInviteListOpen(true);
    },
    [user]
  );

  const closeInviteList = React.useCallback(() => {
    setIsInviteListOpen(false);
    setInviteErr('');
    setInviteClubName('');
    setInviteInitialInvitedIds(new Set());
    setInvitePresetFilters({});
    setInviteSearch('');
    setInviteSearchApplied('');
  }, []);

  const replaceInviteFilters = React.useCallback(
    (filters: any) => {
      const next = {
        ...INVITE_FILTERS_EMPTY,
        ...(filters || {}),
        ...invitePresetFilters,
        forms: Array.isArray(filters?.forms) ? filters.forms.map(String) : [],
        ages: invitePresetFilters.ages || [],
        gender: invitePresetFilters.gender || []
      };
      setInviteFiltersDraft(next);
      setInviteFiltersApplied(next);
      setInvitePage(1);
    },
    [invitePresetFilters]
  );

  const onInviteListSave = React.useCallback(() => {
    if (!inviteProgrammeClubId) return;

    const allowList = (inviteInvitedItems || [])
      .map((x: any) => ({
        userId: String(x?.id || x?._id || ''),
        permissionId: String(x?.permissionId || '')
      }))
      .filter((x: any) => !!x.userId && !!x.permissionId);

    setInviteErr('');
    setSaving(inviteProgrammeClubId, true);

    const payload: any = { allowList };
    if (!inviteInvitesOnly) payload.invitesOnly = true;

    updateProgrammeClub(user, programmeId, inviteProgrammeClubId, payload)
      .then(updated => {
        onProgrammeUpdated(updated);
        setSaving(inviteProgrammeClubId, false);
        setIsInviteListOpen(false);
        setInviteInitialInvitedIds(new Set());
      })
      .catch((e: any) => {
        setInviteErr(getErrMsg(e, 'Failed to save invite list'));
        setSaving(inviteProgrammeClubId, false);
      });
  }, [
    inviteProgrammeClubId,
    inviteInvitedItems,
    inviteInvitesOnly,
    setSaving,
    updateProgrammeClub,
    user,
    programmeId,
    onProgrammeUpdated
  ]);

  const inviteEligibleItems = React.useMemo(() => {
    const eligibleNormalized = (Array.isArray(inviteEligible) ? inviteEligible : [])
      .map((u: any) => ({
        ...u,
        id: String(u?.id || u?._id || ''),
        permissionId: String(u?.permissionId || u?.permission?.id || u?.permission?._id || '')
      }))
      .filter((x: any) => !!x.id && !!x.permissionId);

    // элементы из буфера удалений — показываем в правой панели с клиентской фильтрацией
    const removedNormalized = (Array.isArray(inviteItemsRemoveBuffer) ? inviteItemsRemoveBuffer : [])
      .map((u: any) => ({
        ...u,
        id: String(u?.id || u?._id || ''),
        permissionId: String(u?.permissionId || u?.permission?.id || u?.permission?._id || '')
      }))
      .filter((x: any) => !!x.id && !!x.permissionId);

    const invitedIds = new Set((inviteInvitedItems || []).map((x: any) => String(x?.id || x?._id || '')));

    const selectedInvitedIds = new Set((inviteInvitedSelected || []).map((x: any) => String(x?.id || x?._id || '')));

    const fFirst = String(inviteFiltersApplied?.firstName || '')
      .trim()
      .toLowerCase();
    const fLast = String(inviteFiltersApplied?.lastName || '')
      .trim()
      .toLowerCase();
    const commonSearch = inviteSearchApplied.trim().toLowerCase();
    const selectedGenders = Array.isArray(inviteFiltersApplied?.gender) ? inviteFiltersApplied.gender.map(String) : [];

    const filteredRemoved = removedNormalized.filter((x: any) => {
      const id = String(x.id);
      if (invitedIds.has(id) || selectedInvitedIds.has(id)) {
        return false;
      }
      if (
        fFirst &&
        !String(x.firstName || '')
          .toLowerCase()
          .includes(fFirst)
      ) {
        return false;
      }
      if (
        fLast &&
        !String(x.lastName || '')
          .toLowerCase()
          .includes(fLast)
      ) {
        return false;
      }
      if (
        commonSearch &&
        !`${String(x.firstName || '')} ${String(x.lastName || '')}`.toLowerCase().includes(commonSearch)
      ) {
        return false;
      }
      if (selectedGenders.length > 0 && !selectedGenders.includes(String(x.gender || ''))) return false;
      return true;
    });

    const mergedById: Record<string, any> = {};

    eligibleNormalized.forEach((x: any) => {
      mergedById[String(x.id)] = x;
    });

    filteredRemoved.forEach((x: any) => {
      mergedById[String(x.id)] = x;
    });

    return getPersonListSortedByName(
      Object.keys(mergedById)
        .map((k: string) => mergedById[k])
        .filter((x: any) => {
          const id = String(x.id);
          return !invitedIds.has(id) && !selectedInvitedIds.has(id);
        })
    );
  }, [
    inviteEligible,
    inviteItemsRemoveBuffer,
    inviteInvitedItems,
    inviteInvitedSelected,
    inviteFiltersApplied,
    inviteSearchApplied
  ]);

  const inviteInvitedItemsSorted = React.useMemo(() => {
    const raw = Array.isArray(inviteInvitedItems) ? inviteInvitedItems : [];
    const normalized = raw
      .map((u: any) => ({
        ...u,
        id: String(u?.id || u?._id || ''),
        permissionId: String(u?.permissionId || u?.permission?.id || u?.permission?._id || '')
      }))
      .filter((x: any) => !!x.id && !!x.permissionId);

    return getPersonListSortedByName(normalized);
  }, [inviteInvitedItems]);

  const onInviteAddClick = React.useCallback(() => {
    const toAdd = inviteEligibleSelected || [];
    if (!toAdd.length) return;

    // убираем из inviteInitialInvitedIds — они больше не в invited
    setInviteInitialInvitedIds(prev => {
      const next = new Set(prev);
      toAdd.forEach((x: any) => next.delete(String(x?.id || '')));
      return next;
    });

    setInviteInvitedItems(prev => {
      const existing = new Set((prev || []).map((x: any) => String(x.id)));
      const merged = [...(prev || [])];
      toAdd.forEach((x: any) => {
        const id = String(x?.id || '');
        if (!id || existing.has(id)) return;
        merged.push(x);
        existing.add(id);
      });
      return merged;
    });

    setInviteItemsRemoveBuffer(prev =>
      (prev || []).filter((x: any) => !toAdd.some((a: any) => String(a.id) === String(x.id)))
    );

    setInviteItemsAddBuffer(prev => {
      const removeBufferIds = new Set((inviteItemsRemoveBuffer || []).map((x: any) => String(x.id)));
      const existing = new Set((prev || []).map((x: any) => String(x.id)));
      const next = [...(prev || [])];
      toAdd.forEach((x: any) => {
        const id = String(x?.id || '');
        if (!id || existing.has(id) || removeBufferIds.has(id)) return;
        next.push(x);
        existing.add(id);
      });
      return next;
    });

    setInviteEligibleSelected([]);
  }, [inviteEligibleSelected, inviteItemsRemoveBuffer]);

  const onInviteRemoveClick = React.useCallback(() => {
    const toRemove = inviteInvitedSelected || [];
    if (!toRemove.length) return;

    // возвращаем в inviteInitialInvitedIds — они снова не в invited
    setInviteInitialInvitedIds(prev => {
      const next = new Set(prev);
      toRemove.forEach((x: any) => next.add(String(x?.id || x?._id || '')));
      return next;
    });

    setInviteInvitedItems(prev => {
      const removeIds = new Set(toRemove.map((x: any) => String(x.id)));
      return (prev || []).filter((x: any) => !removeIds.has(String(x.id)));
    });

    setInviteItemsAddBuffer(prev =>
      (prev || []).filter((x: any) => !toRemove.some((r: any) => String(r.id) === String(x.id)))
    );

    setInviteItemsRemoveBuffer(prev => {
      const existing = new Set((prev || []).map((x: any) => String(x.id)));
      const next = [...(prev || [])];
      toRemove.forEach((x: any) => {
        const id = String(x?.id || '');
        if (!id || existing.has(id)) return;
        next.push(x);
        existing.add(id);
      });
      return next;
    });

    setInviteInvitedSelected([]);
  }, [inviteInvitedSelected]);

  const onInviteAddSingleItem = React.useCallback(
    (item: any) => {
      if (!item) return;

      const id = String(item?.id || item?._id || '');
      const isFromRemoveBuffer = (inviteItemsRemoveBuffer || []).some((x: any) => String(x.id) === id);

      setInviteInitialInvitedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      setInviteInvitedItems(prev => {
        const existing = new Set((prev || []).map((x: any) => String(x.id)));
        if (!id || existing.has(id)) return prev || [];
        return [...(prev || []), item];
      });

      setInviteItemsRemoveBuffer(prev => (prev || []).filter((x: any) => String(x.id) !== id));

      if (!isFromRemoveBuffer) {
        setInviteItemsAddBuffer(prev => {
          const existing = new Set((prev || []).map((x: any) => String(x.id)));
          if (!id || existing.has(id)) return prev || [];
          return [...(prev || []), item];
        });
      }

      setInviteEligibleSelected([]);
      setInviteInvitedSelected([]);
    },
    [inviteItemsRemoveBuffer]
  );

  const onInviteRemoveSingleItem = React.useCallback((item: any) => {
    if (!item) return;

    const id = String(item?.id || item?._id || '');

    // возвращаем в inviteInitialInvitedIds
    setInviteInitialInvitedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    setInviteInvitedItems(prev => (prev || []).filter((x: any) => String(x.id) !== id));

    setInviteItemsAddBuffer(prev => (prev || []).filter((x: any) => String(x.id) !== id));

    setInviteItemsRemoveBuffer(prev => {
      const existing = new Set((prev || []).map((x: any) => String(x.id)));
      if (!id || existing.has(id)) return prev || [];
      return [...(prev || []), item];
    });

    setInviteEligibleSelected([]);
    setInviteInvitedSelected([]);
  }, []);

  return {
    isInviteListOpen,
    inviteInvitesOnly,
    inviteErr,
    inviteEligibleLoading,
    inviteEligibleSelected,
    setInviteEligibleSelected,
    inviteInvitedItems,
    inviteInvitedItemsSorted,
    inviteInvitedSelected,
    setInviteInvitedSelected,
    inviteFiltersDraft,
    invitePresetFilters,
    setInviteFiltersDraft,
    inviteItemsFiltersOptions,
    inviteShowEligibleFilter,
    setInviteShowEligibleFilter,
    inviteTotal,
    invitePage,
    setInvitePage,
    setInviteFiltersApplied,
    replaceInviteFilters,
    inviteSearch,
    setInviteSearch,
    inviteEligibleItems,
    inviteInvitedLoading,
    inviteItemsAddBuffer,
    inviteItemsRemoveBuffer,
    openInviteList,
    closeInviteList,
    onInviteListSave,
    loadInvitePage,
    loadAllInviteEligible,
    setInviteEligibleLoading,
    onInviteAddClick,
    onInviteRemoveClick,
    onInviteAddSingleItem,
    onInviteRemoveSingleItem,
    inviteInitialInvitedIds,
    INVITE_FILTERS_EMPTY,
    INVITE_ITEMS_COLUMNS,
    inviteClubName
  };
};
